// Modal: vytvořit/upravit produktovou sadu — vyčleněno ze ProductSetsList.tsx
// kvůli velikosti (pravidlo 5 v CLAUDE.md, max 500 řádků na komponent).

import { ApiError, apiFetch } from '@/lib/apiFetch';
import type { ProductSet, ProductSetItem } from '@sklad-sync/shared';
import { type FormEvent, useEffect, useState } from 'react';
import { ProductSetGiftsSection } from './ProductSetGiftsSection';
import { ProductSetItemsList, type SelectedItem } from './ProductSetItemsList';
import { type Product, useProductSearch } from './productSetSearch';

const ESHOPS: { code: string; label: string }[] = [
  { code: 'v-mart-cz', label: 'CZ' },
  { code: 'v-mart-sk', label: 'SK' },
];

type SetModalProps = {
  set: ProductSet | null;
  eshopId: string;
  onClose: () => void;
  onSaved: (msg: string) => void;
  onError: (msg: string) => void;
};

/** Debounced vyhledávač produktů — sdílené s propojením (stejná tabulka products). */
export function ProductSetModal({ set, eshopId, onClose, onSaved, onError }: SetModalProps) {
  const isEdit = set !== null;
  const [nazev, setNazev] = useState(set?.nazev ?? '');
  const [slevaProcent, setSlevaProcent] = useState(set?.sleva_procent ?? 10);
  const [rezim, setRezim] = useState<'sleva' | 'darek'>(set?.rezim ?? 'sleva');
  const [darekDalsiSleva, setDarekDalsiSleva] = useState(set?.darek_dalsi_sleva ?? 30);
  const [gifts, setGifts] = useState<SelectedItem[]>([]);
  const [aktivni, setAktivni] = useState<boolean>(set ? !!set.aktivni : true);
  const [saving, setSaving] = useState(false);

  // Produkt sady (shell) — přesně jeden.
  const [sadaProduct, setSadaProduct] = useState<Product | null>(
    set
      ? {
          shopitem_id: set.sada_shopitem_id,
          parent_code: set.sada_parent_code ?? null,
          name: set.sada_name ?? `Produkt #${set.sada_shopitem_id}`,
          image_url: set.sada_image_url ?? null,
        }
      : null,
  );
  const sadaSearch = useProductSearch(eshopId);
  const [showSadaDropdown, setShowSadaDropdown] = useState(false);

  // Dárkové balení — volitelný produkt (ano/ne volba ve widgetu), žádný = vypnuto.
  const [giftWrapProduct, setGiftWrapProduct] = useState<Product | null>(
    set?.gift_wrap_shopitem_id
      ? {
          shopitem_id: set.gift_wrap_shopitem_id,
          parent_code: set.gift_wrap_parent_code ?? null,
          name: set.gift_wrap_name ?? `Produkt #${set.gift_wrap_shopitem_id}`,
          image_url: set.gift_wrap_image_url ?? null,
        }
      : null,
  );
  const giftWrapSearch = useProductSearch(eshopId);
  const [showGiftWrapDropdown, setShowGiftWrapDropdown] = useState(false);
  const [giftWrapLabel, setGiftWrapLabel] = useState(set?.gift_wrap_label ?? '');

  // Komponentní produkty sady.
  const [items, setItems] = useState<SelectedItem[]>([]);
  const [loadingItems, setLoadingItems] = useState(isEdit);
  const itemSearch = useProductSearch(eshopId);
  const [showItemDropdown, setShowItemDropdown] = useState(false);

  useEffect(() => {
    if (!set) return;
    (async () => {
      try {
        const data = await apiFetch<{
          set: ProductSet;
          items: ProductSetItem[];
          gifts?: ProductSetItem[];
        }>(`/api/sady/${set.id}`);
        setItems(
          data.items.map((i) => ({
            shopitem_id: i.product_shopitem_id,
            parent_code: i.product_parent_code,
            name: i.product_name ?? `Produkt #${i.product_shopitem_id}`,
            image_url: i.product_image_url,
            item_id: i.id,
            display_name: i.display_name ?? '',
            original_display_name: i.display_name ?? '',
            original_order: i.display_order,
          })),
        );
        setGifts(
          (data.gifts ?? []).map((g) => ({
            shopitem_id: g.product_shopitem_id,
            parent_code: g.product_parent_code,
            name: g.product_name ?? `Produkt #${g.product_shopitem_id}`,
            image_url: g.product_image_url,
            item_id: g.id,
            display_name: g.display_name ?? '',
            original_display_name: g.display_name ?? '',
            original_order: g.display_order,
          })),
        );
      } catch (e: unknown) {
        onError(e instanceof ApiError ? e.message : 'Chyba načítání položek sady');
      } finally {
        setLoadingItems(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [set?.id]);

  function addItem(p: Product) {
    if (items.find((s) => s.shopitem_id === p.shopitem_id)) return;
    setItems((prev) => [
      ...prev,
      {
        shopitem_id: p.shopitem_id,
        parent_code: p.parent_code,
        name: p.name,
        image_url: p.image_url,
        item_id: null,
        display_name: '',
        original_display_name: '',
        original_order: null,
      },
    ]);
    itemSearch.setQuery('');
    setShowItemDropdown(false);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!sadaProduct) {
      onError('Vyber produkt sady v Shoptetu');
      return;
    }
    if (rezim === 'darek' && gifts.length === 0) {
      onError('V režimu dárek přidej aspoň jeden dárek na výběr');
      return;
    }
    // Dárky se ukládají vždy celým seznamem v pořadí (nahradí původní).
    const giftsBody = gifts.map((g) => ({
      product_shopitem_id: g.shopitem_id,
      display_name: g.display_name.trim() || null,
    }));
    setSaving(true);
    try {
      let setId: number;
      if (isEdit) {
        await apiFetch(`/api/sady/${set.id}`, {
          method: 'PATCH',
          body: {
            nazev: nazev.trim(),
            sada_shopitem_id: sadaProduct.shopitem_id,
            sleva_procent: slevaProcent,
            rezim,
            darek_dalsi_sleva: darekDalsiSleva,
            gifts: giftsBody,
            aktivni,
            gift_wrap_shopitem_id: giftWrapProduct?.shopitem_id ?? null,
            gift_wrap_label: giftWrapLabel.trim() || null,
          },
        });
        setId = set.id;
      } else {
        const created = await apiFetch<{ id: number }>('/api/sady', {
          method: 'POST',
          body: {
            nazev: nazev.trim(),
            sada_shopitem_id: sadaProduct.shopitem_id,
            sleva_procent: slevaProcent,
            rezim,
            darek_dalsi_sleva: darekDalsiSleva,
            gifts: giftsBody,
            aktivni,
            eshop_id: eshopId,
            gift_wrap_shopitem_id: giftWrapProduct?.shopitem_id ?? null,
            gift_wrap_label: giftWrapLabel.trim() || null,
          },
        });
        setId = created.id;
      }

      // Při editu smaž položky, které už v seznamu nejsou.
      if (isEdit) {
        const fresh = await apiFetch<{ items: ProductSetItem[] }>(`/api/sady/${setId}`);
        const keepIds = new Set(items.filter((i) => i.item_id !== null).map((i) => i.item_id));
        for (const i of fresh.items) {
          if (!keepIds.has(i.id)) {
            await apiFetch(`/api/sady/${setId}/items/${i.id}`, { method: 'DELETE' });
          }
        }
      }

      for (let i = 0; i < items.length; i++) {
        const it = items[i];
        if (!it) continue;
        if (it.item_id === null) {
          await apiFetch(`/api/sady/${setId}/items`, {
            method: 'POST',
            body: {
              product_shopitem_id: it.shopitem_id,
              display_order: i,
              display_name: it.display_name.trim() || null,
            },
          });
        } else {
          // Jen to, co se změnilo — název ve widgetu a/nebo pořadí (přetažení).
          const body: { display_name?: string | null; display_order?: number } = {};
          if (it.display_name !== it.original_display_name) {
            body.display_name = it.display_name.trim() || null;
          }
          if (it.original_order !== i) body.display_order = i;
          if (Object.keys(body).length > 0) {
            await apiFetch(`/api/sady/${setId}/items/${it.item_id}`, { method: 'PATCH', body });
          }
        }
      }

      onSaved(isEdit ? 'Sada upravena' : 'Sada vytvořena');
    } catch (e: unknown) {
      onError(e instanceof ApiError ? e.describe() : 'Uložení selhalo');
    } finally {
      setSaving(false);
    }
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const inputCls =
    'w-full text-sm text-text placeholder:text-text-muted bg-bg border border-border rounded-lg px-3.5 py-2.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary-100 transition-all';

  return (
    <div
      className="fixed inset-0 z-40 flex items-start justify-center bg-black/40 p-4 overflow-y-auto"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-surface rounded-xl shadow-card-hover w-full max-w-xl reveal my-8">
        <form onSubmit={submit} className="p-6 space-y-4">
          <h2 className="text-lg font-bold text-text">
            {isEdit ? 'Upravit sadu' : 'Nová sada'}{' '}
            <span className="text-xs font-semibold text-text-muted bg-bg border border-border rounded px-1.5 py-0.5 align-middle">
              {ESHOPS.find((e) => e.code === eshopId)?.label ?? eshopId}
            </span>
          </h2>

          <div>
            <label className="text-sm font-medium text-text-secondary block mb-1.5" htmlFor="nazev">
              Interní název <span className="text-danger">*</span>
            </label>
            <input
              id="nazev"
              type="text"
              value={nazev}
              onChange={(e) => setNazev(e.target.value)}
              required
              autoFocus
              placeholder="Např. Letní dárková sada"
              className={inputCls}
            />
            <p className="text-xs text-text-muted mt-1">
              Jen pro orientaci v Tools — zákazník vidí název produktu v Shoptetu.
            </p>
          </div>

          {/* Produkt sady v Shoptetu — přesně jeden. */}
          <div className="relative">
            <span className="text-sm font-medium text-text-secondary block mb-1.5">
              Produkt sady v Shoptetu <span className="text-danger">*</span>
            </span>
            {sadaProduct ? (
              <div className="flex items-center gap-2.5 bg-bg border border-border rounded-lg px-3 py-2">
                {sadaProduct.image_url ? (
                  <img
                    src={sadaProduct.image_url}
                    alt=""
                    className="w-8 h-8 rounded object-contain bg-surface flex-shrink-0"
                  />
                ) : (
                  <div className="w-8 h-8 rounded bg-surface border border-border flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-text truncate">{sadaProduct.name}</div>
                  <div className="text-xs text-text-muted font-mono">
                    {sadaProduct.parent_code ?? '—'} · #{sadaProduct.shopitem_id}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setSadaProduct(null)}
                  className="text-xs font-medium text-text-secondary hover:text-danger cursor-pointer"
                >
                  Změnit
                </button>
              </div>
            ) : (
              <>
                <input
                  type="text"
                  value={sadaSearch.query}
                  onChange={(e) => {
                    sadaSearch.setQuery(e.target.value);
                    setShowSadaDropdown(true);
                  }}
                  onFocus={() => setShowSadaDropdown(true)}
                  placeholder="Hledat produkt podle kódu nebo názvu…"
                  className={inputCls}
                />
                {showSadaDropdown && sadaSearch.query.trim().length >= 2 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-surface border border-border rounded-lg shadow-card-hover max-h-72 overflow-y-auto z-10">
                    {sadaSearch.searching && (
                      <div className="px-3 py-2 text-sm text-text-muted">Hledám…</div>
                    )}
                    {!sadaSearch.searching && sadaSearch.results.length === 0 && (
                      <div className="px-3 py-2 text-sm text-text-muted">Žádné výsledky.</div>
                    )}
                    {!sadaSearch.searching &&
                      sadaSearch.results.map((p) => (
                        <button
                          key={p.shopitem_id}
                          type="button"
                          onClick={() => {
                            setSadaProduct(p);
                            setShowSadaDropdown(false);
                          }}
                          className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-bg cursor-pointer text-left transition-colors"
                        >
                          {p.image_url ? (
                            <img
                              src={p.image_url}
                              alt=""
                              className="w-8 h-8 rounded object-cover bg-bg flex-shrink-0"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded bg-bg border border-border flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-text truncate">{p.name}</div>
                            <div className="text-xs text-text-muted font-mono">
                              {p.parent_code ?? '—'} · #{p.shopitem_id}
                            </div>
                          </div>
                        </button>
                      ))}
                  </div>
                )}
              </>
            )}
            <p className="text-xs text-text-muted mt-1">
              „Shell" produkt, co si založíš v Shoptetu pod jménem sady — na jeho detailu pak poběží
              widget pro výběr velikostí.
            </p>
          </div>

          {/* Režim: sleva % za kompletní sadu, nebo plná cena + dárek ZDARMA na výběr. */}
          <div>
            <span className="text-sm font-medium text-text-secondary block mb-1.5">
              Výhoda sady
            </span>
            <div className="flex gap-2">
              {(
                [
                  ['sleva', 'Sleva %'],
                  ['darek', 'Dárek ZDARMA na výběr'],
                ] as const
              ).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setRezim(value)}
                  className={`text-sm font-medium px-3.5 py-2 rounded-lg border transition-colors cursor-pointer ${
                    rezim === value
                      ? 'bg-primary text-white border-primary'
                      : 'bg-bg text-text-secondary border-border hover:border-primary'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {rezim === 'sleva' ? (
            <div>
              <label
                className="text-sm font-medium text-text-secondary block mb-1.5"
                htmlFor="sleva"
              >
                Sleva (%)
              </label>
              <input
                id="sleva"
                type="number"
                min={0}
                max={100}
                step={0.5}
                value={slevaProcent}
                onChange={(e) => setSlevaProcent(Number(e.target.value))}
                className={`${inputCls} max-w-[140px]`}
              />
            </div>
          ) : (
            <ProductSetGiftsSection
              eshopId={eshopId}
              gifts={gifts}
              onChange={setGifts}
              extraPct={darekDalsiSleva}
              onExtraPct={setDarekDalsiSleva}
              inputCls={inputCls}
            />
          )}

          {/* Dárkové balení — volitelné, "žádné" = vypnuto. */}
          <div className="relative">
            <span className="text-sm font-medium text-text-secondary block mb-1.5">
              Dárkové balení (volitelné)
            </span>
            {giftWrapProduct ? (
              <div className="flex items-center gap-2.5 bg-bg border border-border rounded-lg px-3 py-2">
                {giftWrapProduct.image_url ? (
                  <img
                    src={giftWrapProduct.image_url}
                    alt=""
                    className="w-8 h-8 rounded object-contain bg-surface flex-shrink-0"
                  />
                ) : (
                  <div className="w-8 h-8 rounded bg-surface border border-border flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-text truncate">{giftWrapProduct.name}</div>
                  <div className="text-xs text-text-muted font-mono">
                    {giftWrapProduct.parent_code ?? '—'} · #{giftWrapProduct.shopitem_id}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setGiftWrapProduct(null)}
                  className="text-xs font-medium text-text-secondary hover:text-danger cursor-pointer"
                >
                  Odebrat
                </button>
              </div>
            ) : (
              <>
                <input
                  type="text"
                  value={giftWrapSearch.query}
                  onChange={(e) => {
                    giftWrapSearch.setQuery(e.target.value);
                    setShowGiftWrapDropdown(true);
                  }}
                  onFocus={() => setShowGiftWrapDropdown(true)}
                  placeholder="Hledat produkt podle kódu nebo názvu…"
                  className={inputCls}
                />
                {showGiftWrapDropdown && giftWrapSearch.query.trim().length >= 2 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-surface border border-border rounded-lg shadow-card-hover max-h-72 overflow-y-auto z-10">
                    {giftWrapSearch.searching && (
                      <div className="px-3 py-2 text-sm text-text-muted">Hledám…</div>
                    )}
                    {!giftWrapSearch.searching && giftWrapSearch.results.length === 0 && (
                      <div className="px-3 py-2 text-sm text-text-muted">Žádné výsledky.</div>
                    )}
                    {!giftWrapSearch.searching &&
                      giftWrapSearch.results.map((p) => (
                        <button
                          key={p.shopitem_id}
                          type="button"
                          onClick={() => {
                            setGiftWrapProduct(p);
                            giftWrapSearch.setQuery('');
                            setShowGiftWrapDropdown(false);
                          }}
                          className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-bg cursor-pointer text-left transition-colors"
                        >
                          {p.image_url ? (
                            <img
                              src={p.image_url}
                              alt=""
                              className="w-8 h-8 rounded object-cover bg-bg flex-shrink-0"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded bg-bg border border-border flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-text truncate">{p.name}</div>
                            <div className="text-xs text-text-muted font-mono">
                              {p.parent_code ?? '—'} · #{p.shopitem_id}
                            </div>
                          </div>
                        </button>
                      ))}
                  </div>
                )}
              </>
            )}
            {giftWrapProduct && (
              <input
                type="text"
                value={giftWrapLabel}
                onChange={(e) => setGiftWrapLabel(e.target.value)}
                placeholder={`Název ve widgetu (výchozí: ${giftWrapProduct.name})`}
                className={`${inputCls} mt-2`}
              />
            )}
            <p className="text-xs text-text-muted mt-1">
              Zobrazí se jako další krok vedle velikostí (Ano/Ne), výchozí stav nevybráno. Při "Ano"
              se produkt přidá do košíku spolu se sadou.
            </p>
          </div>

          {/* Komponentní produkty */}
          <div className="relative">
            <span className="text-sm font-medium text-text-secondary block mb-1.5">
              Produkty v sadě
            </span>
            <input
              type="text"
              value={itemSearch.query}
              onChange={(e) => {
                itemSearch.setQuery(e.target.value);
                setShowItemDropdown(true);
              }}
              onFocus={() => setShowItemDropdown(true)}
              placeholder="Hledat produkt podle kódu nebo názvu…"
              className={inputCls}
            />
            {showItemDropdown && itemSearch.query.trim().length >= 2 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-surface border border-border rounded-lg shadow-card-hover max-h-72 overflow-y-auto z-10">
                {itemSearch.searching && (
                  <div className="px-3 py-2 text-sm text-text-muted">Hledám…</div>
                )}
                {!itemSearch.searching && itemSearch.results.length === 0 && (
                  <div className="px-3 py-2 text-sm text-text-muted">Žádné výsledky.</div>
                )}
                {!itemSearch.searching &&
                  itemSearch.results.map((p) => {
                    const already = items.find((s) => s.shopitem_id === p.shopitem_id);
                    return (
                      <button
                        key={p.shopitem_id}
                        type="button"
                        onClick={() => !already && addItem(p)}
                        disabled={!!already}
                        className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-bg cursor-pointer text-left disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        {p.image_url ? (
                          <img
                            src={p.image_url}
                            alt=""
                            className="w-8 h-8 rounded object-cover bg-bg flex-shrink-0"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded bg-bg border border-border flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-text truncate">{p.name}</div>
                          <div className="text-xs text-text-muted font-mono">
                            {p.parent_code ?? '—'} · #{p.shopitem_id}
                          </div>
                        </div>
                        {already && <span className="text-xs text-text-muted">už přidán</span>}
                      </button>
                    );
                  })}
              </div>
            )}

            {loadingItems && <div className="text-xs text-text-muted mt-2">Načítám položky…</div>}
            {!loadingItems && items.length > 0 && (
              <ProductSetItemsList items={items} onChange={setItems} />
            )}
          </div>

          <div>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={aktivni}
                onChange={(e) => setAktivni(e.target.checked)}
                className="w-4 h-4 rounded border-border accent-primary cursor-pointer"
              />
              <span className="text-sm font-medium text-text">Aktivní</span>
            </label>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="text-sm font-medium text-text-secondary hover:text-text bg-bg hover:bg-surface-hover border border-border rounded-lg px-4 py-2 cursor-pointer transition-colors"
            >
              Zrušit
            </button>
            <button
              type="submit"
              disabled={saving || !nazev.trim() || !sadaProduct}
              className="bg-action hover:bg-action-dark text-white text-sm font-semibold rounded-lg px-5 py-2 transition-colors cursor-pointer disabled:opacity-50"
            >
              {saving ? 'Ukládám…' : isEdit ? 'Uložit' : 'Vytvořit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
